import React, { useState, useEffect } from 'react';
import { MatchDetails } from '../../../shared/services/matchDetails';
import DatePicker from '../../../shared/components/ui/DatePicker';
import TimePicker from '../../../shared/components/ui/TimePicker';
import SearchableDropdown from '../../../shared/components/ui/SearchableDropdown';
import { useTournaments } from '../../../shared/hooks/useTournaments';
import { useOfficials } from '../../../shared/hooks/useOfficials';
import { useSeasonsByTournament } from '../../../shared/hooks/useSeasons';

interface MatchDetailsTabProps {
  matchDetails: MatchDetails;
  onUpdate?: (updates: Partial<MatchDetails>) => void;
  onOfficialsUpdate?: (role: string, officialId: string) => void;
}

const MatchDetailsTab: React.FC<MatchDetailsTabProps> = ({ matchDetails, onUpdate, onOfficialsUpdate }) => {
  const { tournaments, loading: tournamentsLoading } = useTournaments();
  const { officials, loading: officialsLoading } = useOfficials();
  const { seasons, loading: seasonsLoading } = useSeasonsByTournament(matchDetails.tournament?.id || '');

  // Helper function to convert UTC date to local date string
  const getLocalDateString = (utcDateString: string | null) => {
    if (!utcDateString) return '';
    const date = new Date(utcDateString);
    return date.toISOString().split('T')[0];
  };

  // Helper function to convert UTC date to local time string
  const getLocalTimeString = (utcDateString: string | null) => {
    if (!utcDateString) return '';
    const date = new Date(utcDateString);
    return date.toTimeString().slice(0, 5);
  };

  // Local state for form values
  const [formData, setFormData] = useState({
    date: getLocalDateString(matchDetails.start_date),
    time: getLocalTimeString(matchDetails.start_date),
    tournament_id: matchDetails.tournament?.id || '',
    name: matchDetails.name || '',
    venue: matchDetails.venue || '',
    season_id: matchDetails.season?.id || '',
    officials: {
      referee: matchDetails.match_officials?.find(o => o.role === 'referee')?.official_id || '',
      assistant1: matchDetails.match_officials?.find(o => o.role === 'assistant1')?.official_id || '',
      assistant2: matchDetails.match_officials?.find(o => o.role === 'assistant2')?.official_id || '',
      var: matchDetails.match_officials?.find(o => o.role === 'var')?.official_id || '',
      var_assistant: matchDetails.match_officials?.find(o => o.role === 'var_assistant')?.official_id || '',
      fourth: matchDetails.match_officials?.find(o => o.role === 'fourth')?.official_id || ''
    }
  });

  // Update form data when matchDetails changes
  useEffect(() => {
    setFormData({
      date: getLocalDateString(matchDetails.start_date),
      time: getLocalTimeString(matchDetails.start_date),
      tournament_id: matchDetails.tournament?.id || '',
      name: matchDetails.name || '',
      venue: matchDetails.venue || '',
      season_id: matchDetails.season?.id || '',
      officials: {
        referee: matchDetails.match_officials?.find(o => o.role === 'referee')?.official_id || '',
        assistant1: matchDetails.match_officials?.find(o => o.role === 'assistant1')?.official_id || '',
        assistant2: matchDetails.match_officials?.find(o => o.role === 'assistant2')?.official_id || '',
        var: matchDetails.match_officials?.find(o => o.role === 'var')?.official_id || '',
        var_assistant: matchDetails.match_officials?.find(o => o.role === 'var_assistant')?.official_id || '',
        fourth: matchDetails.match_officials?.find(o => o.role === 'fourth')?.official_id || ''
      }
    });
  }, [matchDetails]);



  // Helper function to convert local date/time to UTC ISO string
  const convertLocalToUTC = (dateStr: string, timeStr: string) => {
    // Create a date object in local timezone
    const localDate = new Date(`${dateStr}T${timeStr}:00`);
    // Convert to UTC ISO string
    return localDate.toISOString();
  };

  // Handle form updates
  const handleFormUpdate = async (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Call parent update function if provided
    if (onUpdate) {
      if (field === 'date' || field === 'time') {
        // Combine date and time for start_date, converting to UTC
        const currentDate = field === 'date' ? value : formData.date;
        const currentTime = field === 'time' ? value : formData.time;
        const utcDateTime = convertLocalToUTC(currentDate, currentTime);
        onUpdate({ start_date: utcDateTime });
      } else if (field === 'tournament_id') {
        onUpdate({ tournament_id: value });
      } else if (field === 'name') {
        onUpdate({ name: value });
      } else if (field === 'venue') {
        onUpdate({ venue: value });
      } else if (field === 'season_id') {
        onUpdate({ season_id: value } as any);
      }
    }
  };

  // Handle official updates
  const handleOfficialUpdate = (role: string, officialId: string) => {
    setFormData(prev => ({
      ...prev,
      officials: {
        ...prev.officials,
        [role]: officialId
      }
    }));

    // Call parent update function if provided
    if (onOfficialsUpdate) {
      onOfficialsUpdate(role, officialId);
    }
  };

  return (
    <div className="space-y-6">
      {/* Match Information Section */}
      <div className="bg-white rounded-lg border border-gray-200 p-3 hover:shadow-md transition-shadow mb-3">
        <div className="flex items-center space-x-2 mb-3">
          <div className="text-base">📋</div>
          <h3 className="text-sm font-semibold text-gray-900">Match Information</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="space-y-2">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Match No.</label>
              <input 
                type="text" 
                value={formData.name}
                onChange={(e) => handleFormUpdate('name', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                placeholder="Enter match number"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Tournament</label>
              <SearchableDropdown
                value={formData.tournament_id}
                onChange={(tournamentId) => handleFormUpdate('tournament_id', tournamentId)}
                options={tournaments}
                placeholder="Select tournament"
                className="w-full"
                loading={tournamentsLoading}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Start Time</label>
              <TimePicker
                value={formData.time}
                onChange={(time) => handleFormUpdate('time', time)}
                placeholder="Select start time"
                className="w-full"
              />
            </div>
          </div>
          <div className="space-y-2">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Date</label>
              <DatePicker
                value={formData.date}
                onChange={(date) => handleFormUpdate('date', date)}
                placeholder="Select date"
                className="w-full"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Venue</label>
              <input 
                type="text" 
                value={formData.venue}
                onChange={(e) => handleFormUpdate('venue', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                placeholder="Enter venue"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Season</label>
              <SearchableDropdown
                options={seasons.map(season => ({
                  id: season.id,
                  name: season.name,
                  value: season.id,
                  label: season.name
                }))}
                value={formData.season_id}
                onChange={(value) => handleFormUpdate('season_id', value)}
                placeholder="Select season"
                loading={seasonsLoading}
                className="w-full"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Match Officials Section */}
      <div className="bg-white rounded-lg border border-gray-200 p-3 hover:shadow-md transition-shadow mb-3">
        <div className="flex items-center space-x-2 mb-3">
          <div className="text-base">👨‍⚖️</div>
          <h3 className="text-sm font-semibold text-gray-900">Officials</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="space-y-2">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Referee</label>
              <SearchableDropdown
                value={formData.officials.referee}
                onChange={(officialId) => handleOfficialUpdate('referee', officialId)}
                options={officials}
                placeholder="Select referee"
                className="w-full"
                loading={officialsLoading}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Assistant Ref #1</label>
              <SearchableDropdown
                value={formData.officials.assistant1}
                onChange={(officialId) => handleOfficialUpdate('assistant1', officialId)}
                options={officials}
                placeholder="Select assistant referee"
                className="w-full"
                loading={officialsLoading}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Assistant Ref #2</label>
              <SearchableDropdown
                value={formData.officials.assistant2}
                onChange={(officialId) => handleOfficialUpdate('assistant2', officialId)}
                options={officials}
                placeholder="Select assistant referee"
                className="w-full"
                loading={officialsLoading}
              />
            </div>
          </div>
          <div className="space-y-2">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">VAR</label>
              <SearchableDropdown
                value={formData.officials.var}
                onChange={(officialId) => handleOfficialUpdate('var', officialId)}
                options={officials}
                placeholder="Select VAR official"
                className="w-full"
                loading={officialsLoading}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">VAR Assistant</label>
              <SearchableDropdown
                value={formData.officials.var_assistant}
                onChange={(officialId) => handleOfficialUpdate('var_assistant', officialId)}
                options={officials}
                placeholder="Select VAR assistant"
                className="w-full"
                loading={officialsLoading}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Fourth Official</label>
              <SearchableDropdown
                value={formData.officials.fourth}
                onChange={(officialId) => handleOfficialUpdate('fourth', officialId)}
                options={officials}
                placeholder="Select fourth official"
                className="w-full"
                loading={officialsLoading}
              />
            </div>
          </div>
        </div>
      </div>


    </div>
  );
};

export default MatchDetailsTab;
